import React, { useState, useEffect, Fragment } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { toWords } from 'number-to-words';
import { toCapitalCase, isBrowser } from '@helpers/utils';

import Container from '@common/Container';
import Section from '@common/Section';
import Row from '@common/Row';
import Img from '@common/Image';
import Button from '@common/Button';
import Link from '@common/Link';
import Heading from '@common/Heading';
import ArrowUp from '@images/summit/arrow_up_from_line.svg';

import * as styles from './event-summit-agenda.module.scss';

const EventSummitAgenda = ({ data }) => {
  const {
    title,
    showTitle,
    list,
    bgColour
  } = data;
  const [dateFilters, setDateFilters] = useState(null);
  const [toggle, setToggle] = useState(null);
  const [filteredListByDateHour, setFilteredListByDateHour] = useState([]);
  const [pageBottomText, setpageBottomText] = useState(null);
  const convertDayToText = () => {
    const findIndex = dateFilters?.findIndex(x => x === toggle);
    // Return event if its the last day
    if (findIndex === dateFilters?.length-1) {
      return 'Event';
    }
    const convertToWords = toWords(findIndex+1);
    return toCapitalCase(convertToWords);
  }
  const handleScrollToTop = (e) => {
    e.preventDefault();
    isBrowser() && window.scrollTo(0, 0);
  }
  useEffect(() => {
    const newFilters = [];
    list.forEach(x => {
      // Loop through list and sort into list by date
      const date = format(new Date(x.startTime), 'EEE MMM d');
      // Check if this date is in the array
      if (newFilters.findIndex(x => x === date) === -1) {
        newFilters.push(date)
      }
    });
    setDateFilters(newFilters);
    setToggle(newFilters[0])
  }, [list]);
  useEffect(() => {
    if (toggle) {
      const listFilteredByDate = list.filter(x => format(new Date(x.startTime), 'EEE MMM d') === toggle);
      const listFilteredByHour = [];
      listFilteredByDate.forEach(x => {
        if (listFilteredByHour.findIndex(y => y.time === x.startTime) === -1) {
          const hourlySessions = listFilteredByDate.filter(y => y.startTime === x.startTime);
          const newObject = {
            time: x.startTime,
            sessions: hourlySessions
          }
          listFilteredByHour.push(newObject)
        }
      });

      // Sort by start time
      listFilteredByHour.sort(function compare(a, b) {
        const dateA = new Date(a.time);
        const dateB = new Date(b.time);
        return dateA - dateB;
      });

      // List to be displayed on the page
      setFilteredListByDateHour(listFilteredByHour);

      // Bottom text
      const day = convertDayToText(toggle);
      const bottomText = `End of Day ${day}`;
      setpageBottomText(bottomText);
    }
  }, [toggle]);
  return (
    <Section className={clsx({[`bg-${bgColour} py-5`]: bgColour })}>
      <Container>
        {(showTitle && title) && <Heading type='h2' className='mx-auto mb-4 col-lg-10 text-center'>{title}</Heading>}
        {dateFilters &&
          <Row style={{position: 'relative', marginTop: '-50px', marginBottom: '55px', zIndex: 1}}>
            <div className='d-flex col flex-column flex-sm-row px-sm-0 bg-light-teal'>
              {dateFilters.map((date, index) => (
                <Button
                  key={index}
                  onClick={() => setToggle(date)}
                  copy={date}
                  addClass={clsx('fs-14 mb-3 mb-sm-0 shadow-none', styles.btnToggle,{[styles.btnToggleActive]:toggle === date})}
                />
              ))}
            </div>
          </Row>
        }
        <Row className='border-top border-bottom py-1 mb-4'>
          <div className='col-3 col-lg-1'>{'Time'}</div>
          <div className='col d-flex justify-content-between'>
            <div>{'Session'}</div>
            <div>{'*all times are ET'}</div>
          </div>
        </Row>
        {filteredListByDateHour &&
          <Row className={styles.listSummitAgenda}>
            {filteredListByDateHour.map((event, index) => (
              <div key={index} className='d-flex flex-row mb-3 pb-3 border-bottom'>
                <div className='col-3 col-lg-1'>
                  <p className='fs-16 text-uppercase position-relative'>
                    {event.time &&
                      <span>{format(new Date(event.time), 'p')}</span>
                    }
                  </p>
                </div>
                {event.sessions &&
                  <Row className='w-100'>
                    {event.sessions.map((session, index) => (
                      <Link key={index} to={`/bts-summit/agenda${session.slug}`} className='d-flex col no-hover text-dark-grey-4'>
                        <Row className={clsx('d-flex flex-fill flex-lg-nowrap px-3 py-4 rounded mx-0', styles.cardBg, {'mt-3 mt-lg-0':index > 0})}>
                          <div className={clsx('d-flex flex-column justify-content-between', {'col-lg-8 pe-lg-3': event.sessions.length <= 1})}>
                            <div className='mb-3 mb-lg-5'>
                              {session.title &&
                                <Heading type='h2' className='show-hover'>{session.title}</Heading>
                              }
                              {session.tags &&
                                <div className='d-flex flex-wrap'>
                                  {session.tags.map((tag, index) => (
                                    <div key={index} className={clsx('col-auto border border-dark-grey-4 rounded fw-medium text-uppercase mb-1 me-1',
                                      styles.summitTags, {
                                        [styles.summitTagHighlight]: tag.toLowerCase() === 'free ceu',
                                        [styles.summitTagCustomersOnly]: tag.toLowerCase() === 'customers only'
                                      })}
                                    >{tag}</div>
                                  ))}
                                </div>
                              }
                              {session.speakers && !session.showSpeakerImages &&
                                <p>
                                  {'With'}
                                  {session.speakers.map((speaker, index) => (
                                    <Fragment key={index}>
                                      {`${(index > 0 ? ' and ' : ' ')}${speaker.name}`}
                                    </Fragment>
                                  ))}
                                </p>
                              }
                            </div>
                            <p className='text-uppercase mb-3 mb-lg-0'>
                              {session.startTime &&
                                <span>{format(new Date(session.startTime), 'EEE p')}</span>
                              }
                              {session.endTime &&
                                <span>{` - ${format(new Date(session.endTime), 'p')}`}</span>
                              }
                            </p>
                          </div>
                          {(session.speakers && session.showSpeakerImages) &&
                            <div className='col d-flex justify-content-lg-end'>
                              <Row>
                                {session.speakers.map((speaker, index) => (
                                  <div key={index} className='col d-flex flex-column' style={{maxWidth: '212px'}}>
                                    {speaker.image &&
                                      <Img
                                        loading='eager'
                                        image={speaker.image.gatsbyImageData}
                                        file={speaker.image.file}
                                        alt={speaker.image.description}
                                        imgStyle={{ width: '100%' }}
                                        style={{ aspectRatio: '1 / 1'}}
                                        className='mb-2 rounded'
                                      />
                                    }
                                    <div className='text-start'>
                                      {speaker.title &&
                                        <p className='mb-0'>
                                          <span>{speaker.title}</span>
                                          {speaker.company && <span>{` @ ${speaker.company}`}</span>}
                                        </p>
                                      }
                                      {speaker.name && <p>{speaker.name}</p>}
                                    </div>
                                  </div>
                                ))}
                              </Row>
                            </div>
                          }
                        </Row>
                      </Link>
                    ))}
                  </Row>
                }
                
              </div>
            ))}
          </Row>
        }
        <div className='col-lg-10 mx-auto mt-2'>
          <div className='fs-44 fw-medium'>{pageBottomText}</div>
          <button onClick={handleScrollToTop} className='btn fs-24'>
            <img src={ArrowUp} alt=''/>
            <span className='ms-1 text-decoration-underline'>{'Back to top'}</span>
          </button>
        </div>
      </Container>
    </Section>
  )
}

export default EventSummitAgenda
